/* import __COLOCATED_TEMPLATE__ from './market-input-searchable.hbs.js'; */
var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _class;
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
/**
## MarketInputSearchable

`market-input-searchable` combines the functionality of sq-field-input-searchable into a
Market UI elements market-input-text and market-list.

### Hash Properties
This component exposes internal properties as a hash.

### Parameters
@param {Array} options - Required - pass in an array of objects. Each object should have a single property that can be used as a label for searching. Can include an optional alwaysShow boolean attribute
@param {Object} value - Required - pass in a single object for preselection. Pass null if you don't have anything yet. As long as the filtering hooks up, you should see the filter automatically change
@param {Action} valueSelected - Required - or there's little point to this component. It is called when the user selects an option (or types in a new one)

@param {Boolean} standaloneStyling - Optional - set to true to add on styling modifiers to make the input look good standalone outside of a form field
@param {Boolean} isLoading - Optional - set to true when loading is in progress in the parent component - shows a loading spinner at the bottom of the list
@param {Boolean} disabled - Optional - disables the input
@param {Boolean} allowDisabled - Optional - set to true to make options with 'isDisabled' as true be unselectable in the dropdown. Also applies 'form-field-input-searchable__option--disabled' class to disabled options
@param {String} inputLabel - Optional - the label to display in the input
@param {String} labelPath - Optional - the property of each object that the component should key off in terms of its label - defaults to `label`
@param {String} placeholder - Optional - the placeholder to use
@param {String} maxlength - Optional -  the maximum number of characters to allow in the input field
@param {String} nullStateText - Optional - this text will appear in the list when nothing is matched
@param {Object} defaultOption - Optional - when passed in, pressing "enter" when no option is selected will send this option. The first option will no longer be pre-selected.
@param {Boolean} shouldHighlightDefaultOption - Optional - highlights the default option on open when this is true
@param {Boolean} isAdditive - Optional - adds an always-present option to create a new instance of the options being selected. Note that this will prevent showing any null state text.
@param {Boolean} alwaysShowAdditive - Optional - if isAdditive is true, this will always show the additive option in the list.
@param {Boolean} isMultiselect - Optional - allows for multipel values to be selected
@param {String} groupOptionsBy - Optional - string matching property name to be used as headings to group options under
@param {Object} formatter - Optional - when passed in, the user input string, as well as the option labels will be formatted according to the 'apply' function, the formatter will not make user visible changes.
@param {Boolean} [alwaysOpenIfActive] - Optional - If true, opens list element as long as isActive is true
@param {Boolean} [syncFilterOnClose=true] - Optional - If true (the default), the filter will reset to represent the passed in value when the dropdown is closed and neither `valueSelected` nor `inputCancelled` were sent.
@param {Boolean} [clearFilterOnClose=false] - Optional - If true, the input value (filter) will be cleared when the dropdown list closes
@param {Boolean} shouldHideCaret - Optional - If true, the caret will be hidden.
@param {Boolean} renderLazy - Optional - If true, don't render the dropdown list until it's opened. Helps with performance when many options are present, or when rendering many market-input-searchable components on the same page.

@param {Action} inputChanged - Optional - an action called when the user has typed something. It has one parameter - the currently entered filter string from the input. Use this information to load more data lazily from an API or anything else you may need to do
@param {Action} inputCancelled - Optional - called when the user hits escape after opening the dropdown
@param {Action} dropdownOpened - Optional - called when the dropdown opens
@param {Action} dropdownClosed - Optional - called when the dropdown closes
@param {Action} dropdownScrolledToBottom - Optional - called when the dropdown is scrolled to the bottom. This is useful when the parent component wants to load more items
@param {Action} componentBlurred - Optional - called when the user clicked on the document DOM outside the dropdown
@param {Action} createNewOption - Optional - called when the user clicks on the additive option. If a string is returned by this action, it will replace the filter text. If a Promise is returned by this action, it's resolved value will replace the filter text. To be used in conjunction with 'isAdditive'.
*/

import Component from '@ember/component';
import { scheduleOnce } from '@ember/runloop';
import { reads, not } from '@ember/object/computed';
import { action, computed, set, get } from '@ember/object';
import { isEmpty, isBlank, isPresent, isNone } from '@ember/utils';
const KEY_CODE_TAB = 9;
const KEY_CODE_ENTER = 13;
const KEY_CODE_ESC = 27;
const KEY_CODE_SPACE = 32;
const KEY_CODE_UP = 38;
const KEY_CODE_DOWN = 40;

// Adapted from Ember.Array.uniq
function uniq(array) {
  const ret = [];
  const seen = new Set();
  for (const item of array) {
    if (!seen.has(item)) {
      seen.add(item);
      ret.push(item);
    }
  }
  return ret;
}

/*
   Traverse the element's parent nodes, checking if the node has {containerClass}
   Even with a 100 element tree, this executes in under 1ms
*/
function isChildOfClass(element, containerClass) {
  let currentElement = element;
  while (currentElement && currentElement.parentNode) {
    if (currentElement.classList.contains(containerClass)) {
      return true;
    } else if (currentElement.parentNode instanceof DocumentFragment) {
      currentElement = currentElement.parentNode.host;
    } else {
      currentElement = currentElement.parentNode;
    }
  }
  if (currentElement.classList?.contains(containerClass)) {
    return true;
  }
  return false;
}
let MarketInputSearchable = (_dec = computed('defaultOption', 'shouldHighlightDefaultOption', 'optionsList.[]'), _dec2 = computed('optionsList.[]', 'shouldHighlightFirstOption'), _dec3 = computed('alwaysOpenIfActive', 'filter.length', 'isActive', 'isLoading', 'optionsList.length'), _dec4 = computed('isOpen', 'hasEnteredAdditiveOption', 'isReadOnly', 'shouldHideCaret'), _dec5 = computed('options.[]', '_internallyFilteredOptions.[]'), _dec6 = computed('optionsList.[]', 'groupOptionsBy'), _dec7 = computed('optionsList.[]', 'highlightedOption'), _class = class MarketInputSearchable extends Component {
  constructor(...args) {
    super(...args);
    // passed in (required)
    _defineProperty(this, "options", void 0);
    _defineProperty(this, "value", void 0);
    // passed in (optional)
    _defineProperty(this, "inputLabel", void 0);
    _defineProperty(this, "formatter", void 0);
    _defineProperty(this, "disabled", void 0);
    _defineProperty(this, "placeholder", void 0);
    _defineProperty(this, "nullStateText", void 0);
    _defineProperty(this, "defaultOption", void 0);
    _defineProperty(this, "groupOptionsBy", void 0);
    _defineProperty(this, "size", void 0);
    _defineProperty(this, "labelPath", 'label');
    _defineProperty(this, "isLoading", false);
    _defineProperty(this, "isAdditive", false);
    _defineProperty(this, "alwaysShowAdditive", false);
    _defineProperty(this, "isMultiselect", false);
    _defineProperty(this, "syncFilterOnClose", true);
    _defineProperty(this, "standaloneStyling", false);
    _defineProperty(this, "clearFilterOnClose", false);
    _defineProperty(this, "alwaysOpenIfActive", false);
    _defineProperty(this, "shouldHideCaret", false);
    _defineProperty(this, "renderLazy", false);
    // actions passed in (required)
    _defineProperty(this, "valueSelected", void 0);
    // actions passed in (optional)
    _defineProperty(this, "inputChanged", void 0);
    _defineProperty(this, "inputCancelled", void 0);
    _defineProperty(this, "dropdownOpened", void 0);
    _defineProperty(this, "dropdownClosed", void 0);
    _defineProperty(this, "dropdownScrolledToBottom", void 0);
    _defineProperty(this, "classNames", ['market-input-searchable']);
    _defineProperty(this, "classNameBindings", ['isActive:form-field-input-searchable--is-active', 'isOpen:form-field-input-searchable--is-open', 'standaloneStyling:form-field-input-searchable--standalone', 'hasEnteredAdditiveOption:form-field-input-searchable--has-entered-custom-value']);
    // internal state
    _defineProperty(this, "input", void 0);
    _defineProperty(this, "filter", void 0);
    _defineProperty(this, "isActive", false);
    _defineProperty(this, "highlightedOption", void 0);
    _defineProperty(this, "previousValue", reads('value'));
    _defineProperty(this, "previousOptions", reads('options'));
    _defineProperty(this, "shouldHighlightFirstOption", not('defaultOption'));
    // saved refs to event handlers for easy removal
    _defineProperty(this, "boundHandleOutsideClick", void 0);
  }
  didInsertElement(...args) {
    super.didInsertElement(...args);
    const value = this.value;
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    if (value) {
      set(this, 'filter', get(value, this.labelPath));
      set(this, 'highlightedOption', value);
    }
    this._onMouseLeave = () => set(this, 'highlightedOption', null);
    this.element.addEventListener('mouseleave', this._onMouseLeave);
  }
  willDestroyElement(...args) {
    super.willDestroyElement(...args);

    // to clean up state / event handlers if the component is being removed by parent component logic
    document.removeEventListener('click', this.boundHandleOutsideClick);
    this.element.removeEventListener('mouseleave', this._onMouseLeave);
  }
  didReceiveAttrs() {
    super.didReceiveAttrs();
    if (this.value !== this.previousValue) {
      if (this.value) {
        set(this, 'filter', get(this.value, this.labelPath));
      } else {
        set(this, 'filter', '');
      }
      set(this, 'previousValue', this.value);
    }

    // We want to highlight the initial option when the options list changes, but not
    // if any other property changes (e.g. isLoading)
    if (this.options !== this.previousOptions) {
      set(this, 'previousOptions', this.options);
      if (!this.isMultiselect) {
        this._setInitialHighlightedOption();
      }
    }
  }
  _setInitialHighlightedOption() {
    let highlightedOption;
    if (this.highlightDefaultOptionEnabled) {
      highlightedOption = this.defaultOption;
    } else if (this.highlightFirstOptionEnabled) {
      highlightedOption = this.optionsList[0];
    } else {
      return;
    }
    set(this, 'highlightedOption', highlightedOption);
  }
  get highlightDefaultOptionEnabled() {
    const defaultOption = this.defaultOption;
    const shouldHighlightDefaultOption = defaultOption && this.shouldHighlightDefaultOption;
    if (shouldHighlightDefaultOption) {
      const renderedOptions = this.optionsList;
      if (!(renderedOptions && renderedOptions.includes(defaultOption)) && window.console) {
        // We need to use console.warn() because Ember.Logger.warn is deprecated
        // as of Ember 3.2.
        window.console.warn('Cannot highlight defaultOption; does not exist in the optionsList');
      }
    }
    return shouldHighlightDefaultOption;
  }
  get highlightFirstOptionEnabled() {
    return !isEmpty(this.optionsList) && this.shouldHighlightFirstOption;
  }
  get isOpen() {
    return this.isActive && (this.filter?.length > 0 || this.optionsList?.length > 0 || this.isLoading || this.alwaysOpenIfActive);
  }
  shouldShowAdditiveOption() {
    /*
       NB: This is intentionally not a computed property.  'optionsList' is a caller,
       which encourages the adding of 'shouldShowAdditiveOption' as a dependency to
       'optionsList'.  It then becomes easy to create a dependency chain on something
       like 'filter' which would invalidate 'optionsList' on every keypress.
        This leads to performance difficulties on lists of options of size, say, 1000.
        Further, 'shouldShowAdditiveOption' doesn't need to be a computed property
       because the properties it could depend on are: 'filter', 'options', 'isAdditive',
       'labelPath'.
        Since changes to 'filter' cause changes to 'options' (newly allocated memory,
       not necessarily different contents), a dependency on 'filter' is redundant.
       'filter' invalidates on every keypress anyways, so this would never return a
       cached result.
        Similarly, since 'optionsList' is the only thing that checks this and 'optionsList'
       already depends on 'options', there's no need for that here either.
        Finally, we only care about changes to 'isAdditive' and 'labelPath' when
       we want to process changes to the list of options.
        This can simply be a function.
    */
    if (!this.isAdditive) {
      return false;
    }
    if (this.alwaysShowAdditive) {
      return true;
    }
    const filter = this.filter;
    if (isBlank(filter)) {
      return false;
    }
    const lowerCaseFilter = filter.toLowerCase();
    const label = this.labelPath;

    // don't include additive option if filter has match found in options
    const filterMatchesOption = (this.options || []).filter(isPresent).find(item => {
      const itemName = get(item, label);
      if (isBlank(itemName)) {
        return false;
      }
      return itemName.toLowerCase() === lowerCaseFilter;
    });
    return !filterMatchesOption;
  }
  get shouldShowCaret() {
    return !this.isOpen && !this.hasEnteredAdditiveOption && !this.isReadOnly && !this.shouldHideCaret;
  }
  get optionsList() {
    let optionsToRender = [];
    const options = this.options;

    // If we've filtered internally using defaultFilter to narrow things down, render that list
    if (this._internallyFilteredOptions) {
      optionsToRender = this._internallyFilteredOptions;
      // If the component has options passed in, use that list
    } else if (options) {
      optionsToRender = options;
    }
    if (this.shouldShowAdditiveOption() && !options.some(option => option.isAdditive)) {
      return [{
        isAdditive: true
      }, ...optionsToRender];
    } else {
      return optionsToRender;
    }
  }
  get formattedOptions() {
    const groupOptionsBy = this.groupOptionsBy;
    const options = this.optionsList;

    // If no "groupOptionsBy" header prop is specified
    // If there are no options, make sure to return a single empty group.
    if (isEmpty(groupOptionsBy) || options.length === 0) {
      return [{
        options
      }];
    }

    // Map options to groups by specified property
    const headersMap = uniq(options.map(option => get(option, groupOptionsBy)));

    // Reduce the subsequent "headers" array into an array of groups with
    // headers and options. Sort options with no header to the top of the
    // options list.
    return headersMap.reduce((groups, header) => {
      groups.push({
        header,
        options: options.filter(option => get(option, groupOptionsBy) === header)
      });
      return groups;
    }, []).sort((groupA, groupB) => {
      if (!groupA.header) {
        return -1;
      }
      if (!groupB.header) {
        return 1;
      }
      return 0;
    });
  }

  // event handlers
  keyDown(e) {
    switch (e.keyCode) {
      case KEY_CODE_DOWN:
        this.highlightNext(e);
        break;
      case KEY_CODE_UP:
        this.highlightPrevious(e);
        break;
      case KEY_CODE_ENTER:
        this.enterPressed(e);
        break;
      case KEY_CODE_TAB:
        this.closeDropdown();
        if (this.componentBlurred) {
          this.componentBlurred();
        }
        if (this.syncFilterOnClose) {
          this.syncFilter();
        }
        break;
      case KEY_CODE_ESC:
        if (this.isActive) {
          if (this.inputCancelled) {
            this.applyFilter('');
            if (this.inputCancelled) {
              this.inputCancelled();
            }
          } else {
            if (this.componentBlurred) {
              this.componentBlurred();
            }
            if (this.syncFilterOnClose) {
              this.syncFilter();
            }
          }
          this.closeDropdown();
          e.stopPropagation();
        }
        break;
      case KEY_CODE_SPACE:
        if (this.isMultiselect) {
          this.spacePressed(e);
        }
        break;
      default:
        this.openDropdown();
    }
  }

  // dropdown management
  openDropdown() {
    if (this.isActive) {
      return;
    }
    if (this.dropdownOpened) {
      this.dropdownOpened();
    }
    set(this, 'isActive', true);
    scheduleOnce('afterRender', this, this.selectSearchableInput);
    document.addEventListener('click', this.boundHandleOutsideClick);
  }
  selectSearchableInput() {
    this.element.querySelector('.market-input-searchable__input').shadowRoot?.querySelector('input')?.select();
  }
  handleOutsideClick(event) {
    const isShadowDomClick = isChildOfClass(event.target, 'market-input-searchable__option-group');
    // Handle clicks outside the dropdown by committing the selection and then closing the dropdown
    if (this.element !== event.target && !this.element.contains(event.target) && !isShadowDomClick) {
      this.closeDropdown();
      if (this.componentBlurred) {
        this.componentBlurred();
      }
      if (this.syncFilterOnClose) {
        this.syncFilter();
      }
    }
  }
  closeDropdown() {
    if (this.dropdownClosed) {
      this.dropdownClosed();
    }
    document.removeEventListener('click', this.boundHandleOutsideClick);

    // This isn't likely to happen, but it's here as a safeguard incase the parent component
    // destroys this component after a selection has been made.
    if (this.isDestroyed) {
      return;
    }
    if (this.clearFilterOnClose) {
      set(this, 'filter', '');
    }
    set(this, 'isActive', false);
  }

  // selection management
  commitSelection(option) {
    if (!option) {
      if (this.value) {
        set(this, 'filter', get(this.value, this.labelPath));
      } else {
        set(this, 'filter', '');
      }
      return;
    }
    const selectedAdditiveOption = option.isAdditive;
    set(this, 'hasEnteredAdditiveOption', selectedAdditiveOption);
    if (this.allowDisabled && !selectedAdditiveOption && option.isDisabled) {
      return;
    }
    if (this.isAdditive && selectedAdditiveOption) {
      // `createNewOption` can either return Promises or values directly
      const newFilterPromise = Promise.resolve(this.createNewOption(this.filter));
      newFilterPromise.then(newFilter => {
        if (!isNone(newFilter)) {
          set(this, 'filter', newFilter);
        }
      });
      return;
    }

    // NOTE: in theory this shouldn't be necessary. We already have a hook to set filter on value changes in didReceiveAttrs
    // For some reason it doesn't seem to call the method every time it should, and it doesn't work.
    // Need to come back and do more debugging here to be able to remove these lines
    const newValue = get(option, this.labelPath);
    set(this, 'filter', newValue);
    if (this.valueSelected) {
      this.valueSelected(option);
    }
  }
  enterPressed(e) {
    if (this.isOpen) {
      /*
       * It is possible that the current "highlightedOption" is invalid, eg. the user types and then
       * presses enter before loading finishes. In this case the previous highlighted option will
       * still be stored in the property, and will be selected. We actually want to invalidate the
       * previous highlight option in this case (eg. no longer in the optionsList array) and
       * fall back to the defaultOption (if present).
       * For isMultiselect, enter does not change the values, just close the popover
       */
      if (!this.isMultiselect) {
        const highlightedOption = this.highlightedIndex >= 0 ? this.highlightedOption : null;
        const option = highlightedOption || this.defaultOption;
        this.commitSelection(option);
      }
      this.closeDropdown();
      e.stopPropagation();
      e.preventDefault();
    }
  }
  spacePressed(e) {
    if (this.isOpen) {
      const highlightedOption = this.highlightedIndex >= 0 ? this.highlightedOption : null;
      const option = highlightedOption || this.defaultOption;
      this.commitSelection(option);
      e.stopPropagation();
      e.preventDefault();
    }
  }

  // selection management
  get highlightedIndex() {
    return this.optionsList.indexOf(this.highlightedOption);
  }
  highlightIndex(index) {
    const option = this.optionsList[index];
    set(this, 'highlightedOption', option);
  }
  highlightNext(e) {
    this.openDropdown();
    if (this.highlightedIndex < 0) {
      this.highlightIndex(0);
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    const nextIndex = this.highlightedIndex + 1;
    if (nextIndex < this.optionsList?.length) {
      this.highlightIndex(nextIndex);
      e.stopPropagation();
      e.preventDefault();
      scheduleOnce('afterRender', this, this.trackHighlighted);
    }
  }
  highlightPrevious(e) {
    const previousIndex = this.highlightedIndex - 1;
    if (previousIndex > -1) {
      this.highlightIndex(previousIndex);
      e.stopPropagation();
      e.preventDefault();
      scheduleOnce('afterRender', this, this.trackHighlighted);
    }
  }
  trackHighlighted() {
    const highlighted = document.querySelector('.form-field-input-searchable--is-open .market-input-searchable__option--is-highlighted');
    if (!this.isActive || !highlighted) {
      return;
    }
    const pane = this.element.querySelector('.market-input-searchable__popover');
    const paneHeight = pane.offsetHeight;
    const highlightedOptionHeight = highlighted.offsetHeight;
    const highlightedTop = highlighted.offsetTop;
    const highlightedBottom = highlightedTop + highlightedOptionHeight;
    if (highlightedBottom > paneHeight || highlightedTop < 0) {
      highlighted.scrollIntoView({
        block: 'nearest'
      });
    }
  }

  // search/filtering logic
  applyFilter(filter) {
    const options = this.options;
    const labelPath = this.labelPath;
    const format = text => {
      const lowerCased = text.toLowerCase();
      if (this.formatter) {
        return this.formatter.apply(lowerCased);
      } else {
        return lowerCased;
      }
    };
    const formattedInput = format(filter);
    const filtered = options.filter(option => {
      const value = format(get(option, labelPath));
      return option.alwaysShow || value.includes(formattedInput);
    });
    set(this, '_internallyFilteredOptions', filtered);
    set(this, 'filter', filter);
    this._setInitialHighlightedOption();
  }

  // Sync the filter to the current value.
  // If possible, add the text as a new option.
  // Otherwise, reset the text to the previous value.
  syncFilter() {
    const currentLabel = this.value ? get(this.value, this.labelPath) : '';
    const hasNewValue = !isEmpty(this.filter) && this.filter !== currentLabel;
    if (hasNewValue && this.optionsList?.length > 0) {
      this.commitSelection(this.optionsList[0]);
    } else if (this.inputChanged) {
      set(this, 'filter', currentLabel);
    } else {
      this.applyFilter(currentLabel);
    }

    // When the filter is next opened, all the options should be visible, even if something was
    // already selected.
    set(this, '_internallyFilteredOptions', null);
  }
  onScroll() {
    if (this.isLoading || !this.element || !this.dropdownScrolledToBottom || !this.isActive) {
      return;
    }
    const popover = this.element.querySelector('.market-input-searchable__popover');
    const remainingScrollHeight = popover.scrollHeight - popover.scrollTop;
    const popoverHeight = popover.clientHeight;

    // When certain browsers are zoomed, subpixels need to be accounted for, so we need to provide a 1 pixel buffer
    // https://bugs.chromium.org/p/chromium/issues/detail?id=890345
    const scrollBarAtBottom = Math.abs(remainingScrollHeight - popoverHeight) <= 1;
    if (scrollBarAtBottom && this.dropdownScrolledToBottom) {
      this.dropdownScrolledToBottom();
    }
  }
  open() {
    this.openDropdown();
  }
  filterChanged(value) {
    if (this.inputChanged) {
      this.inputChanged(value);
      set(this, 'filter', value);
    } else {
      this.applyFilter(value);
    }
    if (this.popper) {
      // Update Popper instance  so it can reposition based on
      // new filtered list height. This is especially important
      // if the list is currently flipped on top of the input.
      this.popper.scheduleUpdate();
    }
  }
  optionClicked(option) {
    this.commitSelection(option);
    if (!this.isMultiselect) {
      this.closeDropdown();
    }
  }
}, _applyDecoratedDescriptor(_class.prototype, "highlightDefaultOptionEnabled", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "highlightDefaultOptionEnabled"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "highlightFirstOptionEnabled", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "highlightFirstOptionEnabled"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isOpen", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "isOpen"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "shouldShowCaret", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "shouldShowCaret"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "optionsList", [_dec5], Object.getOwnPropertyDescriptor(_class.prototype, "optionsList"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "formattedOptions", [_dec6], Object.getOwnPropertyDescriptor(_class.prototype, "formattedOptions"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "keyDown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "keyDown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "highlightedIndex", [_dec7], Object.getOwnPropertyDescriptor(_class.prototype, "highlightedIndex"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "syncFilter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "syncFilter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onScroll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onScroll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "open", [action], Object.getOwnPropertyDescriptor(_class.prototype, "open"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "filterChanged", [action], Object.getOwnPropertyDescriptor(_class.prototype, "filterChanged"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "optionClicked", [action], Object.getOwnPropertyDescriptor(_class.prototype, "optionClicked"), _class.prototype), _class);
export { MarketInputSearchable as default };